import classNames from "classnames/bind";
import React, { useEffect, useMemo, useState } from "react";
import FontAwesome from "react-fontawesome";
import { ArrowContainer, Popover } from "react-tiny-popover";
import colours from "../constants/index";
import Title from "../Title/Title";
import styles from "./Tooltip.module.scss";
import { TooltipProps } from "./types";

const cx = classNames.bind(styles);

const DEFAULT_MAX_WIDTH = 300;

const Tooltip = ({
  show = false,
  position = ["top", "left"],
  shouldShowCloseButton = false,
  title,
  content,
  width,
  isWhiteTheme = false,
  trigger = "hover",
  align = "center",
  children,
  className,
  contentClassName,
  boundaryElementId,
}: TooltipProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(show);

  useEffect(() => {
    setIsPopoverOpen(show);
  }, [show]);

  const togglePopover = (
    togglePopover: boolean,
    trigger: TooltipProps["trigger"]
  ) => {
    if (trigger === "hover" && !show) {
      setIsPopoverOpen(togglePopover);
    }
  };

  const displayTooltipOnClick = (
    togglePopover: boolean,
    trigger: TooltipProps["trigger"],
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    if (trigger === "click") {
      setIsPopoverOpen(togglePopover);

      event.stopPropagation();
    }
  };

  const parent = useMemo(() => {
    return boundaryElementId
      ? (document.getElementById(boundaryElementId) ?? undefined)
      : undefined;
  }, [boundaryElementId]);

  return (
    <Popover
      isOpen={isPopoverOpen}
      positions={position}
      align={align}
      padding={10}
      onClickOutside={() => setIsPopoverOpen(false)}
      containerParent={parent}
      containerClassName={cx("tooltipContainer")}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor={colours.surface["600"]}
          arrowSize={8}
          arrowStyle={{ boxShadow: "none" }}
          className="popover-arrow-container"
          arrowClassName="popover-arrow"
        >
          <div
            style={
              width ? { maxWidth: width } : { maxWidth: DEFAULT_MAX_WIDTH }
            }
            className={cx("tooltip", isWhiteTheme ? "tooltip--white" : null)}
            role="tooltip"
          >
            <div className={cx("titleContainer")}>
              <Title
                style={
                  isWhiteTheme
                    ? { color: colours.navy["600"] }
                    : { color: colours.surface["0"] }
                }
                className={cx("title")}
                type="subheader"
              >
                {title}
              </Title>
              {shouldShowCloseButton && (
                <div>
                  <button
                    aria-label="close"
                    className={cx(
                      "closeButton",
                      isWhiteTheme ? "closeButton--white" : null
                    )}
                    onClick={() => setIsPopoverOpen(false)}
                  >
                    <FontAwesome name="fal fa-times" />
                  </button>
                </div>
              )}
            </div>
            <div
              role="content"
              className={className ?? ""}
            >
              {content}
            </div>
          </div>
        </ArrowContainer>
      )}
    >
      {/* IMPROVE: refactor to not force a container here and instead provide a modular component that the consumer can use to render the shell */}
      <div
        onMouseOver={() => togglePopover(true, trigger)}
        onMouseLeave={() => togglePopover(false, trigger)}
        onClick={(event) => displayTooltipOnClick(true, trigger, event)}
        className={cx("container", contentClassName)}
        role="target"
      >
        {children}
      </div>
    </Popover>
  );
};

export default Tooltip;
